import React from "react";
import { useTranslation } from "react-i18next";
import { titleCase } from "title-case";
import { Chip, StatusGlyph } from "@outpave/ds";
import { cardStatusTone } from "utils/cards/cardStatusTone";
import StatusType from "enums/CardStatusType";
import CardStatus from "enums/CardStatus";

interface CardStatusChipProps {
  status?: string | null;
  size?: "sm" | "md";
  /** From `isCardPendingTerms`: shows the wait on the terms in place of the card status. */
  pendingTerms?: boolean;
}

/**
 * The one card status pill: tables and drawers all render this, so the same
 * card never looks different depending on where it is read.
 */
export const CardStatusChip: React.FC<CardStatusChipProps> = ({
  status,
  size = "sm",
  pendingTerms = false,
}) => {
  const { t } = useTranslation();

  if (pendingTerms) {
    return (
      <Chip
        tone="info"
        size={size}
        leading={<StatusGlyph status="Pending" tone="info" />}
      >
        {t("Pending Terms")}
      </Chip>
    );
  }

  if (!status) return null;

  const tone = cardStatusTone(status);
  // A canceled card is stored INACTIVE, which is not what it means to a reader.
  const label = titleCase(
    (status === StatusType.INACTIVE ? CardStatus.Canceled : status)
      .toString()
      .toLowerCase(),
  );

  // StatusGlyph is looked up by the untranslated label, so the glyph is right
  // in every locale while the text renders translated.
  return (
    <Chip
      tone={tone}
      size={size}
      leading={<StatusGlyph status={label} tone={tone} />}
    >
      {t(label)}
    </Chip>
  );
};

export default CardStatusChip;
